/**
 * DYNAMIC WHATSAPP WIDGET
 * Adapts messaging based on current site
 */

import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../contexts/SiteContext';

export const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { doctor, bodyPart, specialtyLabel, theme } = useSite();

  useEffect(() => {
    // Show widget after 3 seconds
    const timer = setTimeout(() => setIsVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const whatsappUrl = doctor.whatsappLink || `https://api.whatsapp.com/send/?phone=${doctor.whatsapp}&text=Hi+Dr.+Deepthi+Nandan+Reddy%2C+I%27d+like+to+schedule+an+appointment.&type=phone_number&app_absent=0`;

  if (!isVisible) return null;

  return (
    <>
      {/* WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(true)}
            className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
            aria-label="Open WhatsApp"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-8 h-8 text-white" />
          </Button>
        )}

        {/* WhatsApp Chat Card */}
        {isOpen && (
          <div 
            className="bg-white rounded-lg shadow-2xl w-80 overflow-hidden animate-in slide-in-from-bottom"
            data-testid="whatsapp-widget"
          >
            {/* Header */}
            <div 
              className="text-white p-4 flex items-center justify-between"
              style={{ backgroundColor: theme.primaryHex }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" style={{ color: theme.primaryHex }} />
                </div>
                <div>
                  <h3 className="font-semibold">{doctor.name}</h3>
                  <p className="text-xs opacity-80">{specialtyLabel}</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 bg-gray-50">
              <div className="bg-white p-3 rounded-lg shadow-sm mb-4">
                <p className="text-sm text-gray-700">
                  Hello! 👋
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  Need help with your {bodyPart.toLowerCase()}? I'm here to assist you. Click below to start a conversation on WhatsApp.
                </p>
              </div>
              
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsOpen(false)}
              >
                <Button className="w-full bg-green-500 hover:bg-green-600 text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
              
              <p className="text-xs text-gray-500 text-center mt-3">
                Available: Mon-Sat, 9 AM - 6 PM
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
